import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Building2, Handshake, LoaderCircle, Lock, Mail, MapPin, Phone, UserRound, Globe } from "lucide-react";
import { Logo, EASE } from "@/components/landing/Reveal";
import { API, useAuth, formatApiError } from "@/context/AuthContext";
import { PARTNER_CATEGORIES, priceFor, priceLabel } from "@/lib/partners";

function Field({ icon: Icon, ...props }) {
  return (
    <div className="relative">
      <Icon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input {...props}
        className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-bleu focus:outline-none focus:ring-2 focus:ring-bleu/15" />
    </div>
  );
}

export default function PartnerSignupPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [form, setForm] = useState({
    organization: "", category: PARTNER_CATEGORIES[0].value, is_nonprofit: false,
    name: "", email: "", password: "", phone: "", website: "", city: "", description: "",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const amount = priceFor(form.category, form.is_nonprofit);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/partners/register`, { ...form, accept_terms: acceptTerms }, { withCredentials: true });
      setUser(data);
      if (data.partner?.status === "pending_payment") {
        toast.success("Compte créé ! Dernière étape : activez-le en réglant votre abonnement partenaire.");
        const { data: pay } = await axios.post(`${API}/partners/checkout`, { origin_url: window.location.origin }, { withCredentials: true });
        if (pay.checkout_url) { window.location.href = pay.checkout_url; return; }
      } else {
        toast.success("Bienvenue ! Votre compte partenaire est actif.");
      }
      navigate("/dashboard/partenaire");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/70" data-testid="partner-signup-page">
      <div className="container-integra flex items-center justify-between py-6">
        <Link to="/"><Logo compact /></Link>
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition-colors hover:text-bleu" data-testid="partner-back-home">
          <ArrowLeft className="h-4 w-4" /> Retour à l'accueil
        </Link>
      </div>

      <div className="container-integra pb-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-bleu-light px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-bleu">
            <Handshake className="h-3.5 w-3.5" /> Espace partenaires
          </span>
          <h1 className="mt-5 font-display text-3xl font-extrabold text-slate-900 sm:text-4xl">Devenez partenaire INTEGRA FRANCE</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-500">
            Avocats, associations, centres de formation, recruteurs : présentez votre activité, vos contacts,
            vos tarifs ou vos actions bénévoles directement dans le tableau de bord de nos utilisateurs.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          className="mx-auto mt-10 grid max-w-4xl gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="partner-pricing-grid">
          {PARTNER_CATEGORIES.map((c) => (
            <button key={c.value} type="button" onClick={() => setForm((f) => ({ ...f, category: c.value }))}
              className={`card-integra flex flex-col p-5 text-left transition-all ${form.category === c.value ? "!border-bleu ring-2 ring-bleu/20" : "hover:!border-bleu/40"}`}
              data-testid={`partner-category-${c.value}`}>
              <p className="text-sm font-bold text-slate-900">{c.label}</p>
              <p className="mt-1.5 flex-1 text-[11px] leading-relaxed text-slate-500">{c.desc}</p>
              <p className="mt-3 font-display text-lg font-extrabold text-bleu">{priceLabel(c.price)}</p>
              {c.price > 0 && <p className="text-[10px] font-semibold text-emerald-600">Gratuit si activité bénévole</p>}
            </button>
          ))}
        </motion.div>

        <motion.form onSubmit={submit} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.18, ease: EASE }}
          className="card-integra mx-auto mt-8 max-w-2xl space-y-4 p-8" data-testid="partner-signup-form">
          <h2 className="font-display text-xl font-extrabold text-slate-900">Créer mon compte partenaire</h2>

          <Field icon={Building2} type="text" required placeholder="Nom de l'organisation / du cabinet" value={form.organization} onChange={set("organization")} data-testid="partner-org-input" />

          <div className="rounded-xl border border-slate-100 bg-slate-50 p-4" data-testid="partner-nonprofit-toggle">
            <p className="text-xs font-bold text-slate-700">Votre activité sur INTEGRA FRANCE est :</p>
            <div className="mt-2.5 grid grid-cols-2 gap-2">
              {[{ v: false, l: "Lucrative (payante)" }, { v: true, l: "Bénévole (non lucrative)" }].map((o) => (
                <button key={String(o.v)} type="button" onClick={() => setForm((f) => ({ ...f, is_nonprofit: o.v }))}
                  className={`rounded-lg py-2.5 text-xs font-semibold transition-all ${form.is_nonprofit === o.v ? "bg-bleu text-white shadow-sm" : "bg-white text-slate-500 border border-slate-200"}`}
                  data-testid={`partner-nonprofit-${o.v ? "yes" : "no"}`}>
                  {o.l}
                </button>
              ))}
            </div>
            <p className="mt-3 text-sm font-extrabold text-bleu" data-testid="partner-price-summary">
              Votre tarif : {priceLabel(amount)}
            </p>
          </div>

          <Field icon={UserRound} type="text" required placeholder="Nom du responsable" value={form.name} onChange={set("name")} data-testid="partner-name-input" />
          <Field icon={Mail} type="email" required placeholder="Email professionnel" value={form.email} onChange={set("email")} data-testid="partner-email-input" />
          <Field icon={Lock} type="password" required minLength={8} placeholder="Mot de passe (8 caractères min.)" value={form.password} onChange={set("password")} data-testid="partner-password-input" />
          <div className="grid grid-cols-2 gap-3">
            <Field icon={Phone} type="tel" required placeholder="Téléphone" value={form.phone} onChange={set("phone")} data-testid="partner-phone-input" />
            <Field icon={MapPin} type="text" placeholder="Ville (optionnel)" value={form.city} onChange={set("city")} data-testid="partner-city-input" />
          </div>
          <Field icon={Globe} type="text" placeholder="Site web (optionnel)" value={form.website} onChange={set("website")} data-testid="partner-website-input" />
          <textarea required minLength={10} maxLength={1000} rows={4} value={form.description} onChange={set("description")}
            placeholder="Décrivez votre activité, vos services, vos publics… (visible par les utilisateurs)"
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm placeholder:text-slate-400 focus:border-bleu focus:outline-none focus:ring-2 focus:ring-bleu/15"
            data-testid="partner-description-input" />

          {error && <p className="rounded-xl border border-rouge/20 bg-rose-50 px-4 py-3 text-xs font-medium text-rose-700" data-testid="partner-error-message">{error}</p>}

          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
            <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[#1E3FAE]" data-testid="partner-terms-checkbox" />
            <span className="text-[11px] leading-relaxed text-slate-600">
              J'ai lu et j'accepte les <Link to="/conditions" target="_blank" className="font-bold text-bleu underline">Conditions d'utilisation</Link> et la{" "}
              <Link to="/confidentialite" target="_blank" className="font-bold text-bleu underline">Politique de confidentialité</Link>. <span className="text-rouge">*</span>
            </span>
          </label>

          <button type="submit" disabled={loading || !acceptTerms} className="btn-primary w-full disabled:opacity-60" data-testid="partner-submit-button">
            {loading && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {amount === 0 ? "Créer mon compte partenaire gratuit" : `Créer mon compte — ${priceLabel(amount)}`}
          </button>
          <p className="text-center text-[11px] text-slate-400">
            Les comptes payants sont activés après le règlement sécurisé de l'abonnement (Stripe). Sans engagement, résiliable à tout moment.
          </p>
        </motion.form>
      </div>
    </div>
  );
}
